import type { AdminUser } from '@/api/users';

import { matchesSearch } from './search';

/**
 * Поиск в списке кураторов (28.09.2026, вторая волна админки п. 9): по имени, почте, подтипу, курсу его учеников и
 * группе. Список приходит целиком (кураторов меньше двухсот), поэтому ищем на клиенте.
 */
export function filterCurators(rows: readonly AdminUser[], query: string): AdminUser[] {
  return rows.filter((c) =>
    matchesSearch(
      [
        c.full_name,
        c.email,
        ...(c.curator_subtypes ?? []),
        ...(c.curator_students?.by_marathon ?? []).map((m) => m.title),
        ...(c.curator_groups ?? []).flatMap((g) => [g.name, g.marathon_title]),
      ],
      query,
    ),
  );
}

/**
 * Поиск в «Мои ученики» кабинета куратора (28.09.2026, вторая волна п. 10): по имени, почте, курсу и группе его
 * записей. Список приходит целиком (у куратора бывает 850 учеников) — ищем на клиенте.
 */
export function filterMyStudents<
  T extends { full_name?: string; email?: string; enrollments?: { marathon_title: string; group_name: string }[] },
>(rows: readonly T[], query: string): T[] {
  return rows.filter((s) =>
    matchesSearch(
      [s.full_name, s.email, ...(s.enrollments ?? []).flatMap((e) => [e.marathon_title, e.group_name])],
      query,
    ),
  );
}

/**
 * Сколько учеников у куратора — из строки списка (`curator_students.total`, живые записи, п. 191). Нет поля — не
 * посчиталось: «—», а не ноль.
 *
 * ⚠️ РАНЬШЕ — ОТДЕЛЬНЫМ ЗАПРОСОМ НА КАЖДОГО КУРАТОРА (`/admin/users?role=student&curator_id=`), и тот считал и
 * снятые записи (техдолг 327): у куратора выходило больше учеников, чем в его карточке.
 */
export function curatorStudentsLabel(c: AdminUser): string {
  return c.curator_students ? String(c.curator_students.total) : '—';
}

/**
 * Подпись строки ученика на телефоне (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §4.3): первая запись «курс · группа»
 * и сколько записей ещё. На компьютере записи идут столбиком по одной; в строке 64 px им места нет, а молча отбросить
 * вторую запись нельзя — куратор решит, что ученик учится только на одном курсе.
 */
export function enrollmentLine(enrollments?: readonly { marathon_title: string; group_name: string }[]): {
  line: string;
  more: number;
} {
  const list = (enrollments ?? []).filter((e) => e.marathon_title?.trim() || e.group_name?.trim());
  if (!list.length) return { line: '', more: 0 };
  const first = list[0];
  const line = [first.marathon_title, first.group_name].map((s) => s?.trim()).filter(Boolean).join(' · ');
  return { line, more: list.length - 1 };
}
